import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import queryString from "query-string-for-all";
import { Redirect } from "react-router-dom";
import { withRouter } from "react-router";
import Helmet from "react-helmet";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Trans } from "@lingui/macro";
import {
  SecondaryNav,
  SecondaryNavItem,
  Thirds,
  Center,
} from "@conserveit/blocks";
import { Wrap } from "../../components/Layout/Page";
import { loadAction } from "../../redux/modules/action/actionCreators";
import { updateActionsUrlHistory } from "../../redux/modules/urlHistory/actionCreators";
import {
  loadIssuesRequest,
  ACTION_STATUSES,
  CLOSED_ACTIONS_START_DATE,
} from "../../redux/modules/_BETA/issues";
import {
  updateIsMultiSiteRoute,
  selectActiveSite,
} from "../../redux/modules/_BETA/selections";
import Issues from "../../components/Issues";
import Loading from "../../components/Loading";
import { setShouldRequestIssues } from "../../redux/modules/_BETA/requestSchedule";

function mapStateToProps(state) {
  const { hasLoaded, sites } = state.userSites;

  return {
    appSetupComplete: state.appSetup.appSetupComplete,
    user: state.auth.user,
    error: state.betaIssues.error,
    data: state.betaIssues.data,
    issuesLoaded: state.betaIssues.loaded,
    issuesLoading: state.betaIssues.loading,
    sortFilter: state.betaActionsUI.sortFilter,
    sortDirection: state.betaActionsUI.sortDirection,
    sites,
    hasLoaded,
    shouldRequestIssues: state.requestSchedule.shouldRequestIssues,
    urlHistory: state.urlHistory,
    selectedSites: state.betaSelections.selectedSites,
  };
}

function mapDispatchToProps(dispatch, ownProps) {
  return {
    ...bindActionCreators(
      {
        loadAction,
        loadIssuesRequest,
        setShouldRequestIssues,
        selectActiveSite,
      },
      dispatch
    ),
    updateHistory: () => {
      dispatch(
        updateActionsUrlHistory(
          ownProps.history.location,
          ownProps.history.action
        )
      );
    },
    markAsMultiSite: () => dispatch(updateIsMultiSiteRoute(true)),
  };
}

class IssuesScene extends Component {
  static propTypes = {
    error: PropTypes.object,
    loadIssuesRequest: PropTypes.func.isRequired,
    issuesLoaded: PropTypes.bool,
    issuesLoading: PropTypes.bool,
    sortFilter: PropTypes.string,
    data: PropTypes.array,
    selectedSites: PropTypes.array,
    markAsMultiSite: PropTypes.func.isRequired,
  };

  static defaultProps = {
    issuesLoading: false,
    issuesLoaded: false,
    data: [],
    sortFilter: "UPDATED_NEWEST",
  };

  constructor(props) {
    super(props);
    this.state = {
      filtered: [],
    };
  }

  componentDidMount() {
    const {
      appSetupComplete,
      issuesLoading,
      error,
      shouldRequestIssues,
      history,
      markAsMultiSite,
      data,
    } = this.props;

    markAsMultiSite();

    // If no status present in search params, default to 'open'
    const qs = queryString.parse(history.location.search);
    const { status, action: actionId } = qs;
    if (!status) {
      const stringified = queryString.stringify({
        ...qs,
        status: ACTION_STATUSES.open,
      });
      history.replace({
        search: stringified,
      });
    }

    if (!appSetupComplete) return;
    // Populate the issues list if they've not yet been issuesLoaded
    if (status && !issuesLoading && !error && shouldRequestIssues) {
      this.props.loadIssuesRequest(status);
    }

    // HACK: This is a gross workaround for a half-baked requirement
    if (this.props.data) {
      this.filterMonitoring(status, data);
    }

    if (actionId && !issuesLoading) {
      this.props.loadAction(actionId);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      appSetupComplete,
      issuesLoading,
      error,
      shouldRequestIssues,
      location,
    } = this.props;

    const pastQS = queryString.parse(prevProps.location.search);
    const currentQS = queryString.parse(location.search);

    const pastStatus = pastQS.status;
    const currentStatus = currentQS.status;

    if (!error && shouldRequestIssues && !issuesLoading) {
      // this is handleing the request if the status is closed and sites changed
      if (
        currentStatus === ACTION_STATUSES.closed &&
        currentQS.closedOnStartDate !== undefined
      ) {
        // when closedOnStartDate appears as urlParam
        this.props.loadIssuesRequest(
          currentStatus,
          Number(currentQS.closedOnStartDate)
        );
      } else {
        this.props.loadIssuesRequest(currentStatus);
      }
    } else if (
      currentStatus &&
      pastStatus !== currentStatus &&
      !prevProps.shouldRequestIssues
    ) {
      this.props.setShouldRequestIssues(true);
    }

    if (currentQS.action && prevProps.appSetupComplete !== appSetupComplete) {
      this.props.loadAction(currentQS.action);
      this.props.updateHistory();
    }

    if (currentQS.action && pastQS.action !== currentQS.action) {
      this.props.loadAction(currentQS.action);
      this.props.updateHistory();
    }

    if (appSetupComplete && pastQS.sites !== currentQS.sites) {
      this.handleClearActiveAction();
    }

    if (prevProps.data !== this.props.data) {
      this.filterMonitoring(currentStatus, this.props.data);
    }
  }

  // HACK: use the status to determine whether to display monitoring or regular actions
  // TODO: Stop abusing the status parameter and filtering on the client to fake
  // what should be server functionality.
  filterMonitoring = (status, data) => {
    let actionsByResponsible;

    // Split actions by whether they're assigned to the 'Monitoring' team
    if (status === "monitoring") {
      actionsByResponsible = data.filter(
        (action) => action.responsible === "Monitoring"
      );
    } else {
      actionsByResponsible = data.filter(
        (action) => action.responsible !== "Monitoring"
      );
    }

    // Remove all actions with status 'Rejected'
    const filtered = actionsByResponsible.filter(
      (action) => action.bonfire_status !== "Rejected"
    );
    this.setState({
      filtered,
    });
  };

  handleClearActiveAction = () => {
    const { history } = this.props;
    const qs = queryString.parse(history.location.search);

    const stringified = queryString.stringify({ ...qs, action: undefined });
    history.replace({
      search: stringified,
    });

    this.props.updateHistory();
  };

  handleChangeStatus = (status) => () => {
    const { history } = this.props;
    const qs = queryString.parse(history.location.search);

    // Add the new urlParam closedOnStartDate when viewing closed actions,
    // default value for the urlParam is 12 (months)
    const stringified =
      status === ACTION_STATUSES.closed
        ? queryString.stringify({
          ...qs,
          action: undefined,
          status,
          closedOnStartDate: CLOSED_ACTIONS_START_DATE.LAST_12_MONTHS,
        })
        : queryString.stringify({
          ...qs,
          action: undefined,
          status,
          closedOnStartDate: undefined,
        });

    history.replace({
      search: stringified,
    });

    this.props.updateHistory();
  };

  // Handle the History widget changes in closed actions view
  handleClosedActionsTimeFrameChange = (option) => {
    const { history } = this.props;
    const qs = queryString.parse(history.location.search);

    const stringified = queryString.stringify({
      ...qs,
      action: undefined,
      closedOnStartDate: option,
    });

    history.replace({
      search: stringified,
    });
    this.props.updateHistory();
    this.props.setShouldRequestIssues(true);
    this.props.loadIssuesRequest(ACTION_STATUSES.closed, option);
  };

  render() {
    const {
      user,
      hasLoaded,
      issuesLoading,
      issuesLoaded,
      sortFilter,
      sortDirection,
      location,
      history,
    } = this.props;

    if (!hasLoaded || !user) {
      return null;
    }

    const {
      action: activeActionId,
      status,
      closedOnStartDate,
    } = queryString.parse(location.search);

    if (!user?.groups?.includes("ConserveIt") && status === "monitoring") {
      return <Redirect to="/actions" />;
    }

    if (issuesLoading)
      return (
        <Wrap>
          <Loading />
        </Wrap>
      );

    return (
      <Fragment>
        <Helmet title="Actions" />
        <SecondaryNav hasBottomBorder>
          <Thirds>
            <Center>
              <SecondaryNavItem
                isActive={status === "open"}
                onClick={this.handleChangeStatus("open")}
              >
                <Trans id="actions.open_actions">Open Actions</Trans>
              </SecondaryNavItem>
              <SecondaryNavItem
                isActive={status === "closed"}
                onClick={this.handleChangeStatus("closed")}
              >
                <Trans id="actions.closed_actions">Closed Actions</Trans>
              </SecondaryNavItem>

              {user?.groups?.includes("ConserveIt") && (
                <SecondaryNavItem
                  isActive={status === "monitoring"}
                  onClick={this.handleChangeStatus("monitoring")}
                  data-cy="SecondaryNav-monitoringActions"
                >
                  <Trans id="actions.monitoring_actions">
                    Monitoring Actions
                  </Trans>
                </SecondaryNavItem>
              )}
            </Center>
          </Thirds>
        </SecondaryNav>

        <Wrap>
          <Issues
            activeActionId={activeActionId}
            onHideActionDetails={this.handleClearActiveAction}
            data={this.state.filtered}
            showClosed={status === ACTION_STATUSES.closed}
            loading={issuesLoading}
            loaded={issuesLoaded}
            sortFilter={sortFilter}
            sortDirection={sortDirection}
            updateHistory={this.props.updateHistory}
            history={history}
            status={status}
            hideEdits={!user?.access?.includes("editIssue")}
            handleClosedActionsTimeFrameChange={
              this.handleClosedActionsTimeFrameChange
            }
            closedOnStartDate={
              closedOnStartDate === undefined
                ? closedOnStartDate
                : Number(closedOnStartDate)
            }
          />
        </Wrap>
      </Fragment>
    );
  }
}

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(IssuesScene)
);
